import axios from 'axios';

import { constants } from '../../Helpers/constantsFile';
import { buildPayload } from './settings.rules';

/**
 * Saving the restaurant's own settings.
 *
 * ── The endpoint ─────────────────────────────────────────────────────────
 *
 *     PATCH /api/client/v1/business
 *     Authorization: Bearer <token>
 *
 * No id in the URL. The server works out which business from the token and
 * updates the logged-in manager's own, which is the point — there is no way to
 * aim this at someone else's restaurant even by accident.
 *
 * This is the one place the URL is written down. The public-menu tab writes to
 * the same business and imports it from here, so the day it moves again, it
 * moves once.
 */

/** The one line to change if the route ever moves. */
export const businessUrl = () => `${constants.baseUrl}/business`;

/**
 * The header the server actually reads.
 *
 * `authController.protect` only accepts a header that starts with `Bearer`,
 * and the till already stores the token with that prefix on it
 * (`authenticate.js`: `token: \`Bearer ${...}\``). Every other request in the
 * app passes it through untouched and works. This adds the prefix if it is
 * somehow missing rather than trusting that, because a 401 here looks exactly
 * like "you are not a manager" and would send someone hunting in the wrong
 * place.
 */
export function bearer(token) {
  const value = String(token || '').trim();
  if (!value) return '';
  return /^bearer\s/i.test(value) ? value : `Bearer ${value}`;
}

/**
 * PATCH whatever changed.
 *
 * The payload is diffed here as well as in the panel — the panel builds one to
 * decide whether Save should even be enabled; this builds the one that leaves
 * the machine. It means no future caller can hand this function a body of its
 * own devising.
 *
 * @returns the full updated business. The API's own words: "Replace
 *          user.business with it."
 */
export async function saveBusinessSettings({ form, seed, token }) {
  const payload = buildPayload(form, seed);

  const { data } = await axios.patch(businessUrl(), payload, {
    headers: { authorization: bearer(token) },
    timeout: 25000,
  });

  return data?.data?.updatedBusiness || null;
}

/**
 * Why it did not save, in the three flavours that need different words.
 *
 *   refused  403 — not a manager. Nothing to retry, and "try again" would
 *            send someone round the same loop all afternoon.
 *   invalid  400 — a value broke a model rule. The server's own message is
 *            the useful one; the API documentation says to show it.
 *   failed   anything else — a dead connection, a 500. Worth another go.
 */
export function classifySaveError(error) {
  const status = error?.response?.status;
  if (status === 403 || status === 401) return 'refused';
  if (status === 400) return 'invalid';
  return 'failed';
}

/**
 * The server's own complaint, when it sent one worth reading.
 *
 * A model-rule message ("VAT cannot exceed 100%") is more useful than anything
 * this side could guess, but only when it is short enough to be a sentence
 * rather than a stack trace.
 */
export function serverMessage(error) {
  const message = error?.response?.data?.message;
  if (typeof message !== 'string') return '';
  const trimmed = message.trim();
  return trimmed && trimmed.length <= 200 ? trimmed : '';
}
